import { LinearProgress, Skeleton, Typography } from "@mui/material";
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Trans } from "react-i18next";
import {
  Box,
  Button,
  ContentRepeater,
  FlexColBox,
  Link,
} from "../../../common/components";
import SwipableImageSlider from "../../../common/components/ImageSlider/SwipableImageSlider";
import { createStyles, palette, spacing } from "../../../styles/theme";
import { DESIGN_CHANGE_ENUM } from "../../../utils/ReactHooks";
import { useThemeBreakpoints } from "../../../utils/themeUtils";
import Message, { MessageTypes } from "../../Message/Message";
import "./ProductImage.scss";
import { useDesign } from "../../../context/Design/useDesign";
import { useAuth } from "../../../common/hooks/useAuth";
import DesignService from "../../../service/DesignService";
import { useStudio } from "../../../context/Studio";

const styles = createStyles({
  imageContainer: {
    minHeight: "0",
    position: "relative",
    display: "flex",
    gap: { xs: 0, lg: spacing.wrapperDesktop },
    alignItems: { xs: "center", lg: "flex-start" },
    justifyContent: "center",
    p: {
      xs: `${spacing.regularMobile} 0 0 0`,
      lg: `${spacing.largeDesktop} 0`,
    },
    height: "fit-content",
    maxHeight: "100%",
    width: "100%",
    minWidth: "100%",
    maxWidth: "calc(100vw - 77px - 324px - 32px - 32px - 300px)",
  },
});

interface Props {
  previewContainerRef: React.RefObject<HTMLDivElement | null>;
  onVisibleSideChange?: (side: string) => void;
}

const ProductImage = ({ previewContainerRef, onVisibleSideChange }: Props) => {
  const { isMobile } = useThemeBreakpoints();
  const { design, onDuplicate } = useDesign();
  const { isAdmin } = useAuth();
  const {
    designState: {
      images,
      isUpdating,
      designWarning,
      migrationNeeded,
      colorWarning,
      containsColorsEndOfLife,
      productVersionNeedUpgrade,
      onDesignChange,
      designDecorationsFromDesignState: decorations,
    },
    editorConfig,
    isLoading,
  } = useStudio();

  const { t } = useTranslation();
  const { refetchDesign } = useDesign();

  // Calculate a common image height (desktop) so both sides align vertically
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [targetHeight, setTargetHeight] = useState<number | null>(null);
  const [naturalSizes, setNaturalSizes] = useState<
    Record<string, { width: number; height: number }>
  >({});

  const recalcTargetHeight = useCallback(() => {
    if (isMobile) {
      setTargetHeight(null);
      return;
    }
    const container = containerRef.current;
    const count = images?.length ?? 0;
    if (!container || count === 0) {
      return;
    }

    // Ensure we have intrinsic sizes for all images before calculating
    const sizes = images?.map((img) => naturalSizes[img.side]);
    if (!sizes || sizes.some((s) => !s)) {
      return;
    }

    const containerWidth = container.clientWidth;
    const computed = window.getComputedStyle(container);
    const gapPx = parseFloat((computed.gap as unknown as string) || "0") || 0;
    const totalGaps = gapPx * Math.max(0, count - 1);
    const perImageWidthLimit = (containerWidth - totalGaps) / count;

    const widthLimitedHeights = sizes.map((s) => {
      const aspectRatio = s!.width / s!.height;
      return perImageWidthLimit / aspectRatio;
    });

    const widthConstrained = Math.min(...widthLimitedHeights);
    const viewportConstrained = Math.floor(window.innerHeight * 0.8);
    const finalHeight = Math.max(
      0,
      Math.floor(Math.min(widthConstrained, viewportConstrained))
    );
    setTargetHeight(finalHeight);
  }, [images, isMobile, naturalSizes]);

  useEffect(() => {
    if (isMobile) return;
    const handle = () => recalcTargetHeight();
    handle();
    window.addEventListener("resize", handle);
    const ro = new ResizeObserver(handle);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => {
      window.removeEventListener("resize", handle);
      ro.disconnect();
    };
  }, [isMobile, recalcTargetHeight]);

  // Update current side and notify parent
  const handleSideChange = (side: string) => {
    onVisibleSideChange?.(side);
  };

  if (isLoading) {
    return (
      <FlexColBox flex={1} alignItems={"center"}>
        <Box sx={styles.imageContainer}>
          <ContentRepeater repeatCount={isMobile ? 1 : 2}>
            <Skeleton
              variant="rounded"
              sx={{
                maxHeight: { xs: "50vh", lg: "80vh" },
                maxWidth: "50%",
                width: "100%",
                height: "100%",
              }}
            />
          </ContentRepeater>
        </Box>
      </FlexColBox>
    );
  }

  return (
    <FlexColBox ref={previewContainerRef} flex={1} alignItems={"center"} px={4}>
      <Box ref={containerRef} sx={styles.imageContainer}>
        {isMobile ? (
          <SwipableImageSlider
            images={images?.map((image) => image.url) || []}
            sides={images?.map((image) => image.side) || []}
            onSlideChange={handleSideChange}
          />
        ) : (
          images?.map((image) => (
            <img
              src={image.url}
              alt={image.side}
              key={image.side}
              onLoad={(e) => {
                const el = e.currentTarget as HTMLImageElement;
                setNaturalSizes((prev) => ({
                  ...prev,
                  [image.side]: {
                    width: el.naturalWidth,
                    height: el.naturalHeight,
                  },
                }));
                // Recalculate when a new image loads
                requestAnimationFrame(() => recalcTargetHeight());
              }}
              style={
                !isMobile && targetHeight
                  ? { height: `${targetHeight}px`, width: "auto" }
                  : {
                      maxWidth: "50%",
                      maxHeight: "min(80vh, 100%)",
                      width: "auto",
                      height: "auto",
                    }
              }
            />
          ))
        )}
        {isUpdating && (
          <Box
            position="absolute"
            top={{ xs: 0, lg: "16px" }}
            left={{ xs: 0, lg: "50%" }}
            right={{ xs: 0, lg: undefined }}
            sx={{
              transform: { xs: undefined, lg: "translateX(-50%)" },
              width: { xs: "100%", lg: "120px" },
            }}
          >
            <LinearProgress
              sx={{
                height: { xs: undefined, lg: 4 },
                borderRadius: { xs: "0", lg: "4px" },
                backgroundColor: palette.grey.lightGrey,
                "& .MuiLinearProgress-bar": {
                  backgroundColor: palette.blue.secondary,
                },
              }}
            />
          </Box>
        )}
      </Box>
      <Box
        sx={{
          mb: 2,
          mx: 1,
          zIndex: 1000,
        }}
      >
        {designWarning &&
          !productVersionNeedUpgrade &&
          (migrationNeeded ? (
            <Message
              message={t("generator.warnings.styleOrColorNeedMigration")}
              messageIcon="emergency_home"
              fullWidth={false}
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Button
                onClick={() => onDesignChange(DESIGN_CHANGE_ENUM.REFRESH, null)}
                color="primary"
                size="small"
                sx={{
                  textTransform: "none",
                  backgroundColor: palette.grey.nearBlack,
                  ml: 1.5,
                  whiteSpace: "nowrap",
                  minWidth: "fit-content",
                }}
              >
                {t("general.update")}
              </Button>
            </Message>
          ) : (
            <Message
              message={t("generator.warnings.styleOrColorNoLongerAvailable")}
              messageIcon="emergency_home"
              fullWidth={false}
            />
          ))}
        {productVersionNeedUpgrade && (
          <Message
            message={t("generator.warnings.productVersionNeedUpgrade")}
            messageIcon="emergency_home"
            fullWidth={false}
          />
        )}
        {containsColorsEndOfLife && (
          <Message
            message={t("generator.warnings.colorEndOfLife")}
            messageIcon="emergency_home"
            type={MessageTypes.SUCCESS}
            fullWidth={false}
          />
        )}
        {colorWarning && (
          <Message
            message={t("generator.warnings.colorBleedingWarning")}
            messageIcon="emergency_home"
            fullWidth={false}
          />
        )}
        {decorations?.some((d) => d.deprecated) && (
          <Message
            message={t("generator.warnings.deprecatedDecoration")}
            messageIcon="emergency_home"
            fullWidth={false}
          />
        )}
        {design?.locked && (
          <Message
            message={
              <Typography variant="textRegular">
                <Trans
                  i18nKey={
                    isAdmin
                      ? "studio.admin_locked_design_warning"
                      : "studio.locked_design_warning"
                  }
                  components={
                    isAdmin
                      ? [
                          <Link
                            onClick={async (e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              await DesignService.lockDesign(design.id, false);
                              refetchDesign();
                            }}
                          />,
                          <Link
                            onClick={async (e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              await onDuplicate(
                                editorConfig?.redirectToEditorOnDuplicate
                              );
                            }}
                          />,
                        ]
                      : [
                          <Link
                            onClick={async (e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              await onDuplicate(
                                editorConfig?.redirectToEditorOnDuplicate
                              );
                            }}
                          />,
                        ]
                  }
                />
              </Typography>
            }
            messageIcon="lock"
            type={MessageTypes.INFO}
            fullWidth={false}
          />
        )}
      </Box>
    </FlexColBox>
  );
};
export default memo(ProductImage);
